import type { TranslationKey } from "@msuite/utils/locales";
import { generateId } from "@msuite/utils/strings";
import { useCallback, useEffect, useRef, useState } from "react";
import { localized } from "../localization";
import { toast } from "../toast";

/** Props Interface */
interface UseStreamProps {
	startStreamOnMount?: boolean;
	resetDataOnInput?: boolean;
	method?: "GET" | "POST";
	onMessage?: (chunk: string) => void;
}

interface StartStream {
	body?: object;
	query?: string;
}

/**
 * Turn a refused response into the message the view shows. A coded body —
 * `{ success: false, code, message, params? }`, the shape `errorMiddleware` answers every refusal
 * with — carries an i18n key, which is localised and toasted on the rules
 * `utils/response-handler.ts` applies, so a refused stream reads the same as a refused request:
 * in the user's language, and never for a 401. Any other body keeps the status line, which
 * names something a user cannot act on and is therefore not toasted.
 */
async function readErrorMessage(response: Response): Promise<string> {
	const payload = await response.json().catch(() => null);
	if (!payload?.code) {
		return `Network response was not ok: ${response.status} ${response.statusText}`;
	}
	const message = localized(payload.message as TranslationKey, payload.params);
	if (payload.code !== "unauthorized") toast.error(message);
	return message;
}

/**
 * A custom React hook to handle streaming responses from a Hono backend.
 *
 * @param {string} url - The URL of the Hono streaming endpoint.
 * @returns {UseHonoStreamReturn} - The state and functions to control the stream.
 */
export function useStream(
	url: string,
	{ startStreamOnMount, resetDataOnInput, method = "POST", onMessage }: UseStreamProps,
) {
	/** State */
	const [streamId, setStreamId] = useState<string>(generateId());
	const [data, setData] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [isDone, setIsDone] = useState<boolean>(false);

	const onMessageRef = useRef(onMessage);
	onMessageRef.current = onMessage;

	/**
	 * Resets the hook's state to its initial values.
	 */
	const reset = useCallback(() => {
		setData("");
		setIsLoading(false);
		setError(null);
		setIsDone(false);
		setStreamId(generateId());
	}, []);

	/**
	 * Initiates the fetch request and processes the streaming response.
	 * @param {any} [body] - The request body to send (will be JSON.stringified).
	 */
	const startStream = useCallback(
		async ({ body, query }: StartStream) => {
			reset();
			setIsLoading(true);
			setIsDone(false);
			setStreamId(generateId());

			try {
				const _query = query ? (query.startsWith("?") ? query.slice(1) : query) : "";
				const response = await fetch(`${url}?${_query}`, {
					method,
					headers: {
						"Content-Type": "application/json",
					},
					credentials: "include",
					body: body ? JSON.stringify(body) : undefined,
				});

				if (!response.ok) {
					throw new Error(await readErrorMessage(response));
				}

				if (!response.body) {
					throw new Error("Response body is missing");
				}

				// Get the reader and decoder for processing the stream
				const reader = response.body.getReader();
				const decoder = new TextDecoder("utf-8");

				// Reset mode is newline-framed, and one read can carry several messages or end in
				// the middle of one. The unfinished tail waits here for the rest of itself.
				let pending = "";

				/** Hand over every complete message the buffer holds and show the latest. */
				function emitMessages(text: string, isFinal: boolean) {
					pending += text;
					const parts = pending.split("\n");
					pending = isFinal ? "" : (parts.pop() ?? "");
					const messages = parts.map((part) => part.trim()).filter(Boolean);
					const latest = messages.at(-1);
					if (!latest) return;
					for (const message of messages) onMessageRef.current?.(message);
					setData(latest);
				}

				// Process the stream
				while (true) {
					const { done, value } = await reader.read();
					if (done) {
						if (resetDataOnInput) emitMessages("", true);
						setIsLoading(false);
						setIsDone(true);
						break;
					}
					const chunk = decoder.decode(value, { stream: true });
					setIsLoading(false);
					if (resetDataOnInput) {
						// Reset mode (e.g. status lines): each line is one complete message. It stays
						// whole however the reads fall, because the last one is JSON its reader parses.
						emitMessages(chunk, false);
					} else {
						// Append mode (token streaming): concatenate raw deltas verbatim
						// so inter-word spaces and paragraph breaks survive.
						if (chunk) onMessageRef.current?.(chunk);
						setData((prevData) => prevData + chunk);
					}
				}

				return response;
			} catch (e) {
				console.error("Streaming failed:", e);
				// A stream that failed is a stream that has finished. Without these three lines the
				// view keeps its spinner for ever, which is what a refused request looks like from
				// here: the response never becomes a body to read.
				setError(e instanceof Error ? e.message : "Streaming failed");
				setIsLoading(false);
				setIsDone(true);
			}
		},
		[url, reset, resetDataOnInput, method],
	);

	useEffect(() => {
		if (!startStreamOnMount) return;
		startStream({});
	}, [startStream, startStreamOnMount]);

	return {
		data,
		isLoading,
		error,
		startStream,
		reset,
		isDone,
		streamId,
	};
}

export type Stream = ReturnType<typeof useStream>;
